import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  CheckIcon,
  ChevronLeftIcon,
  PlusIcon,
  UserGroupIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import { CheckIcon as CheckSolid } from '@heroicons/react/24/solid'
import UserAvatar from '../../components/UserAvatar'
import { DateHelper } from '../../utils/dateHelper'

interface Conversation {
  id: string
  partnerId: string
  partnerName: string
  partnerAvatar?: string
  partnerTitle?: string
  lastMessage: string
  lastMessageTime: string
  unreadCount: number
  booking?: {
    id: string
    courseName: string
  }
}

interface Message {
  id: string
  senderId: string
  receiverId: string
  content: string
  type: string
  status: 'sent' | 'delivered' | 'read'
  createdAt: string
  readAt?: string
}

interface Teacher {
  id: string
  userId: string
  title: string
  rating: number
  subjects: string[]
  user: {
    id: string
    firstName: string
    lastName: string
    avatar?: string
  }
}

const StudentMessages = () => {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [sending, setSending] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const messageContainerRef = useRef<HTMLDivElement>(null)

  // 新建对话相关状态
  const [showNewChat, setShowNewChat] = useState(false)
  const [teacherSearchQuery, setTeacherSearchQuery] = useState('')
  const [teachers, setTeachers] = useState<Teacher[]>([])
  const [loadingTeachers, setLoadingTeachers] = useState(false)

  // 从多个可能的地方获取当前用户ID
  const getCurrentUserId = useCallback(() => {
    let userId = localStorage.getItem('userId')
    if (userId) return userId
    
    const token = localStorage.getItem('token')
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]))
        if (payload.id) return payload.id
        if (payload.userId) return payload.userId
        if (payload.sub) return payload.sub
      } catch (e) {
        console.error('解析token失败:', e)
      }
    }
    
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        const user = JSON.parse(userStr)
        if (user.id) return user.id
      } catch (e) {
        console.error('解析user失败:', e)
      }
    }
    
    return null
  }, [])
  
  const currentUserId = getCurrentUserId()

  useEffect(() => {
    fetchConversations()
  }, [])

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.partnerId)
    }
  }, [selectedConversation])

  // 实时更新：每5秒刷新消息
  useEffect(() => {
    if (!selectedConversation) return
    
    const interval = setInterval(() => {
      fetchMessagesQuiet(selectedConversation.partnerId)
    }, 5000)
    
    return () => clearInterval(interval)
  }, [selectedConversation])

  // 实时更新：每10秒刷新对话列表
  useEffect(() => {
    const interval = setInterval(() => {
      fetchConversationsQuiet()
    }, 10000)
    
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/messages/conversations', {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setConversations(data.conversations || [])
      }
    } catch (error) {
      console.error('获取对话列表失败:', error)
    } finally {
      setLoading(false)
    }
  }

  // 静默刷新对话列表（不显示加载状态）
  const fetchConversationsQuiet = async () => {
    try {
      const token = localStorage.getItem('token')
      // 添加时间戳防止 Edge 等浏览器缓存响应
      const response = await fetch(`/api/messages/conversations?_t=${Date.now()}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setConversations(data.conversations || [])
      }
    } catch (error) {
      // 静默失败
    }
  }

  // 搜索老师
  const searchTeachers = async (query: string) => {
    if (!query.trim()) {
      setTeachers([])
      return
    }
    
    setLoadingTeachers(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/teachers?search=${encodeURIComponent(query)}&limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setTeachers(data.teachers || data || [])
      }
    } catch (error) {
      console.error('搜索老师失败:', error)
    } finally {
      setLoadingTeachers(false)
    }
  }

  // 开始新对话
  const startNewConversation = (teacher: Teacher) => {
    const newConv: Conversation = {
      id: `new_${teacher.user.id}`,
      partnerId: teacher.user.id,
      partnerName: `${teacher.user.firstName || ''} ${teacher.user.lastName || ''}`.trim(),
      partnerAvatar: teacher.user.avatar,
      partnerTitle: teacher.title,
      lastMessage: '',
      lastMessageTime: new Date().toISOString(),
      unreadCount: 0
    }
    
    // 检查是否已存在对话
    const existing = conversations.find(c => c.partnerId === teacher.user.id)
    if (existing) {
      setSelectedConversation(existing)
    } else {
      setSelectedConversation(newConv)
    }
    
    setShowNewChat(false)
    setTeacherSearchQuery('')
    setTeachers([])
  }

  // 搜索防抖
  useEffect(() => {
    const timer = setTimeout(() => {
      if (teacherSearchQuery) {
        searchTeachers(teacherSearchQuery)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [teacherSearchQuery])

  const fetchMessages = async (partnerId: string) => {
    setLoadingMessages(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/messages/conversation/${partnerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setMessages(data.messages || [])
        
        const unreadIds = (data.messages || [])
          .filter((m: Message) => m.receiverId === currentUserId && m.status !== 'read')
          .map((m: Message) => m.id)
        
        if (unreadIds.length > 0) {
          markAsRead(unreadIds)
        }
      }
    } catch (error) {
      console.error('获取消息失败:', error)
    } finally {
      setLoadingMessages(false)
    }
  }

  // 静默刷新消息（不显示加载状态）
  const fetchMessagesQuiet = async (partnerId: string) => {
    try {
      const token = localStorage.getItem('token')
      // 添加时间戳防止 Edge 等浏览器缓存响应
      const response = await fetch(`/api/messages/conversation/${partnerId}?_t=${Date.now()}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      })

      if (response.ok) {
        const data = await response.json()
        const newMessages = data.messages || []
        
        // 只在有新消息时更新
        if (newMessages.length !== messages.length) {
          setMessages(newMessages)
          
          const unreadIds = newMessages
            .filter((m: Message) => m.receiverId === currentUserId && m.status !== 'read')
            .map((m: Message) => m.id)
          
          if (unreadIds.length > 0) {
            markAsRead(unreadIds)
          }
        }
      }
    } catch (error) {
      // 静默失败
    }
  }

  const markAsRead = async (messageIds: string[]) => {
    try {
      const token = localStorage.getItem('token')
      await fetch('/api/messages/mark-read', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ messageIds })
      })
      
      setConversations(prev => prev.map(c => 
        c.partnerId === selectedConversation?.partnerId 
          ? { ...c, unreadCount: 0 }
          : c
      ))
    } catch (error) {
      console.error('标记已读失败:', error)
    }
  }

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || sending) return

    const messageContent = newMessage.trim()
    setNewMessage('')
    
    // 乐观更新：立即显示消息
    const tempMessage: Message = {
      id: `temp_${Date.now()}`,
      senderId: currentUserId || '',
      receiverId: selectedConversation.partnerId,
      content: messageContent,
      type: 'text',
      status: 'sent',
      createdAt: new Date().toISOString()
    }
    setMessages(prev => [...prev, tempMessage])

    setSending(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/messages', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          receiverId: selectedConversation.partnerId,
          content: messageContent,
          type: 'text',
          bookingId: selectedConversation.booking?.id
        })
      })

      if (response.ok) {
        const data = await response.json()
        // 替换临时消息
        setMessages(prev => prev.map(m => 
          m.id === tempMessage.id ? data.data : m
        ))
        
        if (inputRef.current) {
          inputRef.current.style.height = '52px'
        }
        inputRef.current?.focus()
        
        setConversations(prev => prev.map(c =>
          c.partnerId === selectedConversation.partnerId
            ? { ...c, lastMessage: messageContent, lastMessageTime: new Date().toISOString() }
            : c
        ))
      } else {
        // 发送失败，移除临时消息
        setMessages(prev => prev.filter(m => m.id !== tempMessage.id))
        const errorData = await response.json()
        alert(errorData.message || '发送失败')
      }
    } catch (error) {
      // 发送失败，移除临时消息
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id))
      console.error('发送消息失败:', error)
      alert('发送失败，请稍后重试')
    } finally {
      setSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const filteredConversations = conversations.filter(c =>
    c.partnerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.booking?.courseName.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // 使用统一的 DateHelper 处理时间（自动转换为设备本地时区）
  const formatTime = (dateStr: string) => DateHelper.formatChatTime(dateStr)
  
  // 判断是否需要显示日期分隔线
  const shouldShowDateSeparator = (currentMsg: Message, prevMsg?: Message) => {
    return DateHelper.shouldShowDateSeparator(prevMsg?.createdAt, currentMsg.createdAt)
  }

  // 日期分隔线格式化
  const formatDateSeparator = (dateStr: string) => DateHelper.formatDateSeparator(dateStr)

  // 获取头像首字母
  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?'
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-emerald-50/40">
      {/* 头部 */}
      <div className="bg-white/90 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-200/50">
                <ChatBubbleLeftRightIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">消息中心</h1>
                <p className="text-sm text-slate-500">与老师保持沟通，获取学习指导</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { fetchConversations(); if (selectedConversation) fetchMessages(selectedConversation.partnerId); }}
                className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-all"
                title="刷新"
              >
                <ArrowPathIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 主内容区 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-3xl shadow-2xl shadow-slate-200/50 border border-slate-100 overflow-hidden h-[calc(100vh-180px)] min-h-[600px] flex">
          {/* 左侧：对话列表 */}
          <div className={`w-full md:w-[340px] lg:w-[380px] border-r border-slate-100 flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
            {/* 搜索框和新建按钮 */}
            <div className="p-4 bg-gradient-to-b from-slate-50 to-white border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="搜索对话..."
                    className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all shadow-sm"
                  />
                </div>
                <button
                  onClick={() => setShowNewChat(true)}
                  className="w-11 h-11 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow-lg shadow-emerald-200/50 hover:shadow-xl hover:scale-105 transition-all"
                  title="新建对话"
                >
                  <PlusIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* 对话列表 */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-40">
                  <div className="w-10 h-10 border-3 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-400 px-6">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center mb-4">
                    <ChatBubbleLeftRightIcon className="w-10 h-10 text-slate-300" />
                  </div>
                  <p className="text-base font-medium text-slate-500">暂无对话</p>
                  <p className="text-sm mt-2 text-center">点击右上角 + 按钮开始新对话</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-50">
                  {filteredConversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => setSelectedConversation(conv)}
                      className={`w-full p-4 flex items-center gap-3.5 hover:bg-gradient-to-r hover:from-emerald-50/50 hover:to-transparent transition-all ${
                        selectedConversation?.partnerId === conv.partnerId 
                          ? 'bg-gradient-to-r from-emerald-50 to-transparent border-l-4 border-emerald-500' 
                          : 'border-l-4 border-transparent'
                      }`}
                    >
                      <div className="relative flex-shrink-0">
                        <UserAvatar
                          avatar={conv.partnerAvatar}
                          firstName={conv.partnerName.split(' ')[0]}
                          lastName={conv.partnerName.split(' ')[1]}
                          size="lg"
                          gradient
                        />
                        {conv.unreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 bg-gradient-to-r from-rose-500 to-pink-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg animate-pulse">
                            {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-bold text-slate-800 truncate">{conv.partnerName || '老师'}</span>
                          <span className="text-[11px] text-slate-400 flex-shrink-0 ml-2 font-medium">
                            {formatTime(conv.lastMessageTime)}
                          </span>
                        </div>
                        {conv.booking && (
                          <p className="text-xs text-emerald-600 font-semibold mb-1 truncate flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                            {conv.booking.courseName}
                          </p>
                        )}
                        <p className={`text-sm truncate ${conv.unreadCount > 0 ? 'text-slate-700 font-semibold' : 'text-slate-500'}`}>
                          {conv.lastMessage || '开始对话吧...'}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 右侧：消息详情 */}
          <div className={`flex-1 flex flex-col bg-gradient-to-b from-slate-50/50 to-white ${!selectedConversation ? 'hidden md:flex' : 'flex'}`}>
            {selectedConversation ? (
              <>
                {/* 对话头部 */}
                <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-4 bg-white/80 backdrop-blur-sm">
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="md:hidden w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition"
                  >
                    <ChevronLeftIcon className="w-5 h-5 text-slate-600" />
                  </button>
                  <UserAvatar
                    avatar={selectedConversation.partnerAvatar}
                    firstName={selectedConversation.partnerName.split(' ')[0]}
                    lastName={selectedConversation.partnerName.split(' ')[1]}
                    size="lg"
                    gradient
                    showOnline
                    isOnline
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-lg text-slate-800">{selectedConversation.partnerName || '老师'}</h3>
                    {selectedConversation.booking ? (
                      <p className="text-sm text-emerald-600 font-medium truncate">{selectedConversation.booking.courseName}</p>
                    ) : (
                      <p className="text-sm text-slate-500">在线</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => fetchMessages(selectedConversation.partnerId)}
                      className="p-2.5 rounded-xl hover:bg-slate-100 text-slate-500 transition-all"
                      title="刷新消息"
                    >
                      <ArrowPathIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* 消息列表 */}
                <div 
                  ref={messageContainerRef}
                  className="flex-1 overflow-y-auto px-5 py-5"
                  style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)' }}
                >
                  {loadingMessages ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 border-3 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
                        <p className="mt-4 text-sm text-slate-500">加载消息中...</p>
                      </div>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400">
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-100 to-teal-50 flex items-center justify-center mb-5 shadow-inner">
                        <ChatBubbleLeftRightIcon className="w-12 h-12 text-emerald-400" />
                      </div>
                      <p className="text-lg font-semibold text-slate-600">开始对话</p>
                      <p className="text-sm mt-2 text-slate-500">发送第一条消息，与老师交流</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg, index) => {
                        // 确保字符串比较
                        const isMe = String(msg.senderId) === String(currentUserId)
                        const showDateSeparator = shouldShowDateSeparator(msg, messages[index - 1])
                        const nextMsg = messages[index + 1]
                        const prevMsg = messages[index - 1]
                        const showAvatar = !isMe && (!nextMsg || String(nextMsg.senderId) === String(currentUserId))
                        const isFirstInGroup = !prevMsg || String(prevMsg.senderId) !== String(msg.senderId)
                        const isLastInGroup = !nextMsg || String(nextMsg.senderId) !== String(msg.senderId)
                        
                        return (
                          <div key={msg.id}>
                            {/* 日期分隔线 */}
                            {showDateSeparator && (
                              <div className="flex items-center justify-center my-6">
                                <div className="flex items-center gap-3">
                                  <div className="h-px w-12 bg-gradient-to-r from-transparent to-slate-200"></div>
                                  <span className="px-4 py-1.5 bg-white text-slate-500 text-xs font-medium rounded-full shadow-sm border border-slate-100">
                                    {formatDateSeparator(msg.createdAt)}
                                  </span>
                                  <div className="h-px w-12 bg-gradient-to-l from-transparent to-slate-200"></div>
                                </div>
                              </div>
                            )}
                            
                            {/* 消息行 */}
                            <div className={`flex items-end gap-3 ${isMe ? 'flex-row-reverse' : 'flex-row'} ${!isLastInGroup ? 'mb-1' : ''}`}>
                              {/* 对方头像 */}
                              {!isMe && (
                                <div className="w-10 h-10 flex-shrink-0">
                                  {showAvatar ? (
                                    <UserAvatar
                                      avatar={selectedConversation.partnerAvatar}
                                      firstName={selectedConversation.partnerName.split(' ')[0]}
                                      lastName={selectedConversation.partnerName.split(' ')[1]}
                                      size="md"
                                      gradient
                                    />
                                  ) : (
                                    <div className="w-10 h-10" />
                                  )}
                                </div>
                              )}
                              
                              {/* 消息气泡 */}
                              <div className={`flex flex-col max-w-[70%] ${isMe ? 'items-end' : 'items-start'}`}>
                                {/* 发送者名称（仅第一条显示） */}
                                {isFirstInGroup && !isMe && (
                                  <span className="text-xs text-slate-500 mb-1.5 ml-1 font-medium">
                                    {selectedConversation.partnerName || '老师'}
                                  </span>
                                )}
                                
                                <div
                                  className={`relative px-4 py-3 ${
                                    isMe
                                      ? `bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-200/30 ${isFirstInGroup ? 'rounded-t-2xl' : 'rounded-t-lg'} ${isLastInGroup ? 'rounded-bl-2xl rounded-br-md' : 'rounded-b-lg'}`
                                      : `bg-white text-slate-800 border border-slate-100 shadow-sm ${isFirstInGroup ? 'rounded-t-2xl' : 'rounded-t-lg'} ${isLastInGroup ? 'rounded-br-2xl rounded-bl-md' : 'rounded-b-lg'}`
                                  }`}
                                >
                                  <p className="text-[15px] leading-relaxed whitespace-pre-wrap break-words">{msg.content}</p>
                                </div>
                                
                                {/* 时间和状态（自动显示设备本地时间） */}
                                {isLastInGroup && (
                                  <div className={`flex items-center gap-2 mt-1.5 px-1 ${isMe ? 'flex-row-reverse' : ''}`}>
                                    <span className="text-[11px] text-slate-400 font-medium">
                                      {DateHelper.formatMessageTime(msg.createdAt)}
                                    </span>
                                    {isMe && (
                                      <span className={msg.status === 'read' ? 'text-emerald-500' : 'text-slate-400'}>
                                        {msg.status === 'read' ? (
                                          <CheckSolid className="w-4 h-4" />
                                        ) : msg.id.startsWith('temp_') ? (
                                          <span className="text-[10px]">发送中...</span>
                                        ) : (
                                          <CheckIcon className="w-4 h-4" />
                                        )}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {/* 自己的消息右侧占位 */}
                              {isMe && <div className="w-10 flex-shrink-0" />}
                            </div>
                          </div>
                        )
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>

                {/* 输入框 */}
                <div className="p-4 bg-white border-t border-slate-100">
                  <div className="flex items-end gap-3">
                    <div className="flex-1 relative">
                      <textarea
                        ref={inputRef}
                        value={newMessage}
                        onChange={(e) => {
                          setNewMessage(e.target.value)
                          e.target.style.height = 'auto'
                          e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px'
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder="输入消息..."
                        className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-[15px] resize-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 focus:bg-white transition-all placeholder:text-slate-400"
                        rows={1}
                        style={{ minHeight: '52px', maxHeight: '150px' }}
                      />
                    </div>
                    <button
                      onClick={sendMessage}
                      disabled={!newMessage.trim() || sending}
                      className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all transform ${
                        newMessage.trim() && !sending
                          ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-200/50 hover:shadow-xl hover:scale-105 active:scale-95'
                          : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      {sending ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <PaperAirplaneIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2 text-center">按 Enter 发送 · Shift + Enter 换行</p>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-8">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-emerald-100 to-teal-50 flex items-center justify-center mb-6 shadow-inner">
                  <ChatBubbleLeftRightIcon className="w-16 h-16 text-emerald-400" />
                </div>
                <h3 className="text-2xl font-bold text-slate-700 mb-3">开始对话</h3>
                <p className="text-base text-slate-500 mb-6 text-center max-w-sm">从左侧选择一个对话，或点击下方按钮开始新的对话</p>
                <button
                  onClick={() => setShowNewChat(true)}
                  className="px-8 py-3.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-2xl font-semibold shadow-lg shadow-emerald-200/50 hover:shadow-xl hover:scale-105 transition-all flex items-center gap-2"
                >
                  <PlusIcon className="w-5 h-5" />
                  新建对话
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 新建对话弹窗 */}
      {showNewChat && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
            {/* 弹窗头部 */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                    <UserGroupIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">新建对话</h3>
                    <p className="text-white/80 text-sm">搜索并选择老师开始对话</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowNewChat(false)
                    setTeacherSearchQuery('')
                    setTeachers([])
                  }}
                  className="w-10 h-10 rounded-xl bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* 搜索框 */}
            <div className="p-4 border-b border-slate-100 bg-slate-50">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={teacherSearchQuery}
                  onChange={(e) => setTeacherSearchQuery(e.target.value)}
                  placeholder="输入老师姓名或学科搜索..."
                  className="w-full pl-12 pr-4 py-3.5 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all shadow-sm"
                  autoFocus
                />
              </div>
            </div>

            {/* 搜索结果 */}
            <div className="max-h-[400px] overflow-y-auto">
              {loadingTeachers ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-10 h-10 border-3 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
                </div>
              ) : teachers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400 px-6">
                  <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                    <UserGroupIcon className="w-8 h-8 text-slate-300" />
                  </div>
                  <p className="text-sm font-medium text-slate-500">
                    {teacherSearchQuery ? '未找到匹配的老师' : '输入关键词搜索老师'}
                  </p>
                  <p className="text-xs mt-2 text-slate-400">可以搜索老师姓名或学科</p>
                </div>
              ) : (
                <div className="py-2">
                  {teachers.map((teacher) => (
                    <button
                      key={teacher.id}
                      onClick={() => startNewConversation(teacher)}
                      className="w-full p-4 flex items-center gap-4 hover:bg-emerald-50 transition-all"
                    >
                      <UserAvatar
                        avatar={teacher.user?.avatar}
                        firstName={teacher.user?.firstName}
                        lastName={teacher.user?.lastName}
                        size="lg"
                        gradient
                      />
                      <div className="flex-1 text-left min-w-0">
                        <p className="font-bold text-slate-800 truncate">
                          {teacher.user?.firstName} {teacher.user?.lastName}
                        </p>
                        <p className="text-sm text-emerald-600 font-medium truncate">{teacher.title || '教师'}</p>
                        {teacher.subjects && teacher.subjects.length > 0 && (
                          <p className="text-xs text-slate-500 truncate mt-0.5">
                            {teacher.subjects.slice(0, 3).join(' · ')}
                          </p>
                        )}
                      </div>
                      {teacher.rating > 0 && (
                        <div className="text-right flex-shrink-0">
                          <div className="text-sm font-bold text-amber-500 flex items-center gap-1">
                            <span>★</span>
                            <span>{(Number(teacher.rating) || 0).toFixed(1)}</span>
                          </div>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StudentMessages
